<script setup>
// T-262 — a alternativa em linha para o mapa de calor. Preferência declarada da Tamara.
//
// O mapa responde "a que horas o movimento acontece", mas responde com uma grade de quadradinhos
// coloridos, e ler intensidade por cor é trabalho. A linha responde a mesma pergunta com a
// forma que os olhos já sabem ler: sobe, tem um pico, desce.
//
// POR HORA DO DIA, e não ao longo do período. É a leitura que o mapa entrega e que nenhuma
// outra tela dá: a tendência ao longo dos dias já é o gráfico do T-253. Aqui o eixo é 0h a 23h,
// com a MÉDIA de cada hora nos dias do período.
//
// Média e não soma: somando, um período de 30 dias mostraria números dez vezes maiores que um
// de 3, e a forma da curva pareceria diferente só por causa do tamanho da janela.
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { fromUnixTime } from 'date-fns';

const props = defineProps({
  heatmapData: { type: Array, default: () => [] },
  isLoading: { type: Boolean, default: false },
});

const { t } = useI18n();

const GRAF = { largura: 720, altura: 200, pad: 28 };

const porHora = computed(() => {
  const soma = Array(24).fill(0);
  const dias = Array.from({ length: 24 }, () => new Set());

  props.heatmapData.forEach(ponto => {
    const data = fromUnixTime(ponto.timestamp);
    const hora = data.getHours();
    soma[hora] += ponto.value || 0;
    dias[hora].add(data.toDateString());
  });

  return soma.map((total, hora) => {
    const quantos = dias[hora].size;
    return quantos ? total / quantos : 0;
  });
});

const grafico = computed(() => {
  const valores = porHora.value;
  if (!props.heatmapData.length) return null;

  const maximo = Math.max(1, ...valores);
  const passo = (GRAF.largura - GRAF.pad * 2) / 23;
  const y = v =>
    GRAF.altura - GRAF.pad - (v / maximo) * (GRAF.altura - GRAF.pad * 2);

  const caminho = valores
    .map((v, h) => `${h === 0 ? 'M' : 'L'} ${GRAF.pad + h * passo} ${y(v)}`)
    .join(' ');

  const pico = valores.indexOf(Math.max(...valores));
  return { caminho, maximo: Math.round(maximo * 10) / 10, pico, passo, y };
});

// Marcas de 3 em 3 horas: 24 rótulos não cabem na largura e viram borrão.
const horasMarcadas = [0, 3, 6, 9, 12, 15, 18, 21];
</script>

<template>
  <div v-if="isLoading" class="py-10 text-sm text-center text-n-slate-11">
    {{ t('HEATMAP_LINHA.CARREGANDO') }}
  </div>
  <div
    v-else-if="!grafico"
    class="py-10 text-sm text-center text-n-slate-11"
  >
    {{ t('HEATMAP_LINHA.VAZIO') }}
  </div>
  <div v-else class="flex flex-col gap-2 px-2 py-3">
    <div class="overflow-x-auto">
      <svg viewBox="0 0 720 200" class="w-full min-w-[480px] h-[200px]">
        <line
          v-for="h in horasMarcadas"
          :key="`grade-${h}`"
          :x1="28 + h * grafico.passo"
          :x2="28 + h * grafico.passo"
          :y1="16"
          :y2="172"
          stroke="currentColor"
          stroke-width="1"
          class="text-n-slate-4"
        />
        <path
          :d="grafico.caminho"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          class="text-n-brand"
        />
      </svg>
    </div>
    <div class="flex justify-between px-1 text-xs text-n-slate-11">
      <span v-for="h in horasMarcadas" :key="`rotulo-${h}`">
        {{ String(h).padStart(2, '0') }}h
      </span>
    </div>
    <p class="text-xs text-n-slate-11">
      {{
        t('HEATMAP_LINHA.RESUMO', {
          hora: String(grafico.pico).padStart(2, '0'),
          media: grafico.maximo,
        })
      }}
    </p>
  </div>
</template>
